import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2, Filter, X } from "lucide-react";

import Navbar from "../components/navigation/Navbar";
import MapComponent from "../components/map/mapComponent";
import SimpleCharts from "../components/charts/charts";
import ViolationList from "../components/dashboard/ViolationList";
import FilterPanel from "../components/filters/FilterPanel";

import { getSessionToken, settings, Client } from "../library/allauth";
import { getCSRFToken } from "../library/django";
import SummaryStats from "../components/kpi/SummaryStats";
import { useRegions } from "../context/RegionsContext";
import { getDefaultDateRange } from "../utils/defaultDateRange";

// Helper functions moved to top level to avoid duplication
const formatDateForBackend = (dateString, timeString) => {
  if (!dateString) return "";
  const date = new Date(dateString + "T" + timeString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const getApiOptions = () => {
  const options = {
    headers: {},
    credentials: settings.withCredentials ? "include" : "same-origin",
  };
  const sessionToken = getSessionToken();
  if (settings.client === Client.BROWSER && sessionToken) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return options;
};

// Build the chart fetch query string. Mirrors kpiUtils.buildQueryParams so
// Dashboard charts and individual KPI pages query the backend identically
// (snake_case aggregation_level, both startDate and endDate sent).
const buildChartQuery = (filters) => {
  const query = new URLSearchParams();
  if (filters.systemId !== undefined && filters.systemId !== "") {
    query.append("systemId", filters.systemId);
  }
  if (filters.startDate && filters.startTime) {
    query.append(
      "startDate",
      formatDateForBackend(filters.startDate, filters.startTime),
    );
  }
  if (filters.endDate && filters.endTime) {
    query.append(
      "endDate",
      formatDateForBackend(filters.endDate, filters.endTime),
    );
  }
  if (filters.aggregationLevel) {
    query.append("aggregation_level", filters.aggregationLevel);
  }
  return query;
};

const UserCountChart = ({ onExpand, onDataLoaded, filters }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    // If Dashboard hasn't initialized filters yet, don't spin forever
    if (!filters?.systemId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const baseUrl = settings.baseUrl || "";
    const query = buildChartQuery(filters);

    try {
      const response = await fetch(
        `${baseUrl}/kpi/get_usercount_pivot/?${query}`,
        getApiOptions(),
      );
      const responseData = await response.json();
      const results =
        responseData.results ||
        responseData.data ||
        (Array.isArray(responseData) ? responseData : []);

      setData(results);

      // IMPORTANT: Trigger the parent update
      if (onDataLoaded) {
        onDataLoaded(results);
      }
    } catch (error) {
      console.error("UserCount fetch error:", error);
      setData([]);
      if (onDataLoaded) onDataLoaded([]);
    } finally {
      setLoading(false);
    }
  }, [filters]); // REMOVED onDataLoaded from here to prevent loops

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="h-full lg:flex-1 bg-white rounded-lg p-3 lg:min-h-0 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">
          Movement Counts Over Time
        </h3>
        <button
          onClick={onExpand}
          className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          title="Expand to full view"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <div className="h-[calc(100%-2.5rem)]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="text-sm text-gray-500">Loading chart...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">No data available</p>
            </div>
          </div>
        ) : (
          <SimpleCharts
            data={data}
            chartType="horizontal"
            showControls={false}
            compact={true}
            height="100%"
          />
        )}
      </div>
    </div>
  );
};

const TurnCountChart = ({ onExpand, filters }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!filters?.systemId) return;
    setLoading(true);

    const baseUrl = settings.baseUrl || "";
    const query = buildChartQuery(filters);

    try {
      const response = await fetch(
        `${baseUrl}/kpi/get_turncount_pivot/?${query}`,
        getApiOptions(),
      );
      const responseData = await response.json();
      const results =
        responseData.results ||
        responseData.data ||
        (Array.isArray(responseData) ? responseData : []);
      setData(results);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="h-full lg:flex-1 bg-white rounded-lg p-3 lg:min-h-0 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">
          Movement Counts
        </h3>
        <button
          onClick={onExpand}
          className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          title="Expand to full view"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <div className="h-[calc(100%-2.5rem)]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="text-sm text-gray-500">Loading chart...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">No data available</p>
              <p className="text-xs text-gray-400">
                Check console for API response
              </p>
            </div>
          </div>
        ) : (
          <SimpleCharts
            data={data}
            showControls={false}
            compact={true}
            height="100%"
            columnMapping={{
              source: ["entry_street", "entry_side", "entry_lane"],
              target: ["exit_street", "exit_side", "exit_lane"],
              value: "value",
            }}
          />
        )}
      </div>
    </div>
  );
};

const TrafficSpeedChart = ({ onExpand, filters }) => {
  // Add filters prop here
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false); // Start false, fetchData will trigger
  const hasFetched = useRef(false);

  // We wrap the fetch logic in useCallback so the trigger is stable
  const fetchData = useCallback(async () => {
    if (!filters?.systemId) return; // Don't fetch if we don't have a siteId yet

    setLoading(true);
    const baseUrl = settings.baseUrl || "";
    const query = buildChartQuery(filters);

    try {
      const response = await fetch(
        `${baseUrl}/kpi/get_traffic_pivot/?${query}`,
        getApiOptions(),
      );
      const responseData = await response.json();
      const results =
        responseData.results ||
        responseData.data ||
        (Array.isArray(responseData) ? responseData : []);
      setData(results);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]); // This function "re-arms" whenever filters change

  // This is the trigger!
  useEffect(() => {
    fetchData();
  }, [fetchData]); // Whenever fetchData is re-armed, run it

  return (
    <div className="h-full lg:flex-1 bg-white rounded-lg p-3 lg:min-h-0 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">
          Speed Distribution
        </h3>
        <button
          onClick={onExpand}
          className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          title="Expand to full view"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <div className="h-[calc(100%-2.5rem)]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="text-sm text-gray-500">Loading chart...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">No data available</p>
            </div>
          </div>
        ) : (
          <SimpleCharts
            data={data}
            chartType="stacked"
            showControls={false}
            compact={true}
            height="100%"
          />
        )}
      </div>
    </div>
  );
};

const SpeedStatsChart = ({ onExpand, filters }) => {
  // Add filters prop here
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false); // Start false, fetchData will trigger
  const hasFetched = useRef(false);

  // We wrap the fetch logic in useCallback so the trigger is stable
  const fetchData = useCallback(async () => {
    if (!filters?.systemId) return; // Don't fetch if we don't have a siteId yet

    setLoading(true);
    const baseUrl = settings.baseUrl || "";
    const query = buildChartQuery(filters);

    try {
      const response = await fetch(
        `${baseUrl}/kpi/get_speed_stats/?${query}`,
        getApiOptions(),
      );
      const responseData = await response.json();
      const results =
        responseData.results ||
        responseData.data ||
        (Array.isArray(responseData) ? responseData : []);
      setData(results);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]); // This function "re-arms" whenever filters change

  // This is the trigger!
  useEffect(() => {
    fetchData();
  }, [fetchData]); // Whenever fetchData is re-armed, run it

  return (
    <div className="h-full lg:flex-1 bg-white rounded-lg p-3 lg:min-h-0 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">
          Speed Statistics
        </h3>
        <button
          onClick={onExpand}
          className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          title="Expand to full view"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <div className="h-[calc(100%-2.5rem)]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="text-sm text-gray-500">Loading chart...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">No data available</p>
            </div>
          </div>
        ) : (
          <SimpleCharts
            data={data}
            chartType="stacked"
            showControls={false}
            compact={true}
            height="100%"
          />
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { allSystems, isLoading: regionsLoading } = useRegions();

  const [mainFilter, setMainFilter] = useState(() => ({
    systemId: "1",
    ...getDefaultDateRange(),
    aggregationLevel: "hourly",
  }));

  // 2. This is for the Charts (only updates on "Apply")
  const [appliedFilter, setAppliedFilter] = useState(mainFilter);

  const [summaryStats, setSummaryStats] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Once allSites loads, resolve the default siteId into a concrete
  // { siteId, streetId } pair so the FilterPanel select and the
  // active-filters chip can find a matching site.
  useEffect(() => {
    if (!allSystems || allSystems.length === 0) return;
    if (mainFilter.streetId != null) return;
    const match = allSystems.find(
      (s) => String(s.system_id) === String(mainFilter.systemId),
    );
    if (!match) return;
    const resolved = { systemId: match.system_id, streetId: match.street_id };
    setMainFilter((prev) => ({ ...prev, ...resolved }));
    setAppliedFilter((prev) => ({ ...prev, ...resolved }));
  }, [allSystems, mainFilter.systemId, mainFilter.streetId]);

  // Calculate statistics from user count data
  const handleUserCountData = (data) => {
    if (!data || data.length === 0) {
      setSummaryStats(null);
      return;
    }

    let totalPedestrians = 0;
    let totalVehicles = 0;
    let totalCyclist = 0;
    let records = data.length;

    data.forEach((item) => {
      totalPedestrians += item.pedestrian_count || item.Pedestrian || 0;
      totalVehicles += item.vehicle_count || item.Vehicle || 0;
      totalCyclist += item.cyclist_count || item.Cyclist || 0;
    });

    const totalCount = totalPedestrians + totalVehicles + totalCyclist;
    const avgPedestrians =
      records > 0 ? (totalPedestrians / records).toFixed(1) : 0;
    const avgVehicles = records > 0 ? (totalVehicles / records).toFixed(1) : 0;

    setSummaryStats({
      totalPedestrians,
      totalVehicles,
      totalCyclist,
      totalCount,
      avgPedestrians: parseFloat(avgPedestrians),
      avgVehicles: parseFloat(avgVehicles),
      records,
    });
  };

  const handleExpandTrafficSpeed = () => {
    sessionStorage.setItem(
      "kpiFilter",
      JSON.stringify({
        ...mainFilter,
        chartType: "stacked",
        chartTitle: "Speed Distribution",
        source: "dashboard",
      }),
    );
    navigate("/kpi/traffic-speed");
  };

  const handleExpandUserCount = () => {
    sessionStorage.setItem(
      "userCountFilter",
      JSON.stringify({
        ...mainFilter,
        chartType: "horizontal",
        chartTitle: "Movement Counts Over Time",
        source: "dashboard",
      }),
    );
    navigate("/kpi/user-count");
  };

  const handleExpandTurnCount = () => {
    sessionStorage.setItem(
      "turnCountFilter",
      JSON.stringify({
        ...mainFilter,
        chartType: "grouped",
        chartTitle: "Movement Counts",
        source: "dashboard",
      }),
    );
    navigate("/kpi/turn-count");
  };

  const handleExpandSpeedStats = () => {
    sessionStorage.setItem(
      "speedStatsFilter",
      JSON.stringify({
        ...mainFilter,
        chartType: "line",
        chartTitle: "Speed Percentiles Over Time",
        source: "dashboard",
      }),
    );
    navigate("/kpi/speed-stats");
  };

  const handleFilterChange = (key, value) => {
    setMainFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = (filters) => {
    if (!filters || Object.keys(filters).length === 0) {
      console.error("Cannot apply empty filters.");
      return;
    }
    setAppliedFilter(filters);
  };

  const handleResetFilters = (resetFilter) => {
    setMainFilter(resetFilter);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  // Get selected site name
  const getSelectedSystemName = () => {
    const system = allSystems.find(
      (s) =>
        String(s.system_id) === String(mainFilter.systemId) &&
        String(s.street_id) === String(mainFilter.streetId),
    );
    return system
      ? `System ${system.system_id}, ${system.street_name}`
      : "No system selected";
  };

  return (
    <>
      <Navbar />

      <div className="min-h-screen lg:h-screen bg-gray-50 pt-14 overflow-y-auto lg:overflow-hidden">
        <div className="p-2 space-y-3">
          {/* Active Filters Display */}

          <div className="bg-white rounded-lg border border-gray-200 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-wrap">
                <h4 className="text-sm font-semibold text-gray-700">
                  Active Filters:
                </h4>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200">
                    <strong>System:</strong> {getSelectedSystemName()}
                  </span>
                  {mainFilter.startDate && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md border border-green-200">
                      <strong>Start:</strong> {mainFilter.startDate}{" "}
                      {mainFilter.startTime}
                    </span>
                  )}
                  {mainFilter.endDate && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-md border border-purple-200">
                      <strong>End:</strong> {mainFilter.endDate}{" "}
                      {mainFilter.endTime}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-50 text-pink-700 text-xs rounded-md border border-pink-200">
                    <strong>Aggregation:</strong> {mainFilter.aggregationLevel}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                <Filter size={16} />
                Show Filters
              </button>
            </div>
          </div>

          {/* Summary Statistics */}
          <SummaryStats stats={summaryStats} />

          {/* Main Dashboard Grid */}
          <div className="grid min-h-0 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 auto-rows-fr">
            {/* Filter Panel - Now in Position 1 (Upper Left Corner) */}
            {showFilters ? (
              <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden max-h-[320px]">
                <FilterPanel
                  filters={mainFilter}
                  systems={allSystems}
                  isLoading={regionsLoading}
                  onFilterChange={handleFilterChange}
                  onApply={handleApplyFilters}
                  onReset={handleResetFilters}
                  onClose={handleCloseFilters}
                />
              </div>
            ) : (
              <></>
            )}
            <UserCountChart
              onExpand={handleExpandUserCount}
              filters={appliedFilter}
              onDataLoaded={handleUserCountData}
            />
            <TurnCountChart
              filters={appliedFilter}
              onExpand={handleExpandTurnCount}
            />
            <SpeedStatsChart
              onExpand={handleExpandSpeedStats}
              filters={appliedFilter}
            />
            <TrafficSpeedChart
              onExpand={handleExpandTrafficSpeed}
              filters={appliedFilter}
            />
            <ViolationList className="bg-white rounded-lg border border-gray-200" />
            <div className="p-3 relative bg-white rounded-lg border border-gray-200 overflow-hidden min-h-[320px]">
              <MapComponent selectedSystemId={mainFilter.systemId} />
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </>
  );
}
